<template>
  <b-container fluid class="bg-white lz-controls">
    <div class="reports-panel">
      <!-- Status tiles. Each one is a filter toggle for the list below and, at
           the same time, the summary for that status. -->
      <div class="status-grid">
        <button
          type="button"
          v-for="statusItem in expenseStatusOpts"
          :key="statusItem.value"
          :id="'report-box-' + statusItem.value.toLowerCase()"
          class="status-tile"
          :class="[
            'status-' + statusItem.value.toLowerCase(),
            { off: !IsStatusOn(statusItem.value) }
          ]"
          v-b-tooltip.hover.bottom="{ variant: 'light' }"
          :title="
            (IsStatusOn(statusItem.value) ? 'Hide ' : 'Show ') +
            statusItem.text.toLowerCase() +
            ' requests'
          "
          @click="ToggleStatus(statusItem.value)">
          <span class="tile-head">
            <span class="tile-dot"></span>
            <span class="tile-label">{{ statusItem.value }}</span>
            <b-icon
              class="tile-toggle"
              :icon="
                IsStatusOn(statusItem.value) ? 'check-circle-fill' : 'circle'
              "></b-icon>
          </span>
          <span class="tile-count">
            {{ StatusTotal(statusItem.value) }}
            <small>{{
              StatusTotal(statusItem.value) === 1 ? 'request' : 'requests'
            }}</small>
          </span>
          <span class="tile-amounts">
            <template v-if="StatusAmounts(statusItem.value).length">
              <span
                v-for="amount in StatusAmounts(statusItem.value)"
                :key="amount.code"
                class="tile-amount">
                {{ amount.symbol }}{{ amount.value }}
              </span>
            </template>
            <span v-else class="tile-amount empty">&mdash;</span>
          </span>
        </button>
      </div>

      <!-- Breakdown of everything currently switched on above, along one
           dimension at a time. Replaces the per-tile stat pickers: one table is
           readable where four 128px scroll boxes were not. -->
      <div class="breakdown">
        <div class="breakdown-head">
          <div class="breakdown-title">
            Breakdown
            <span class="breakdown-sub">
              <template v-if="ActiveStatuses.length">
                <span
                  v-for="status in ActiveStatuses"
                  :key="status"
                  class="status-badge"
                  :class="'status-' + status.toLowerCase()">
                  {{ status.toUpperCase() }}
                </span>
              </template>
              <span v-else class="status-badge status-none">
                NO STATUS SELECTED
              </span>
            </span>
          </div>
          <div class="dim-tabs">
            <button
              type="button"
              v-for="dim in dimensions"
              :key="dim.key"
              class="dim-tab"
              :class="{ active: dimension === dim.key }"
              :disabled="!HasDimensionData(dim.key)"
              @click="dimension = dim.key">
              <b-icon :icon="dim.icon" font-scale="0.95"></b-icon>
              {{ dim.label }}
            </button>
          </div>
        </div>

        <b-table
          v-if="BreakdownRows.length"
          :items="BreakdownRows"
          :fields="BreakdownFields"
          :sort-by="BreakdownSortKey"
          sort-desc
          responsive
          small
          borderless
          table-class="lizard-report-box"
          thead-class="label"
          tbody-class="value">
          <template #cell(name)="data">
            <div
              v-if="dimension === 'projects'"
              class="selected-project"
              :style="'background-color: ' + data.item.color + ' !important'"
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Click to filter by this project"
              @click="projectChanged(data.item)">
              {{ data.item.name }}
            </div>
            <div
              v-else-if="dimension === 'users'"
              class="row-link"
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Click to filter by this person"
              @click="userChanged(data.item)">
              {{ data.item.name }}
            </div>
            <span v-else>{{ data.item.name }}</span>
          </template>
        </b-table>
        <div v-else class="breakdown-empty">
          Nothing to break down &mdash; switch a status on above, or widen the
          filters.
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import ServicesOld from '../../../services/index.vue'

export default {
  name: 'ReportsBox',
  props: {
    expenseStatusOpts: {
      type: Array,
      required: true
    },
    remoteFilters: {
      type: Object,
      default: () => ({})
    },
    rangeEnums: {
      type: Object,
      required: true
    },
    userChanged: {
      type: Function,
      default: () => {}
    },
    projectChanged: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      CurrencySymbol: {
        GBP: '£',
        USD: '$',
        EUR: '€'
      },
      Currencies: ['gbp', 'eur', 'usd'],
      reportsLabelStatus: {
        PENDING: true,
        APPROVED: true,
        REJECTED: false,
        PAID: false
      },
      dimensions: [
        { key: 'users', label: 'People', icon: 'people', name: 'Name' },
        { key: 'projects', label: 'Projects', icon: 'journals', name: 'Project' },
        { key: 'categories', label: 'Categories', icon: 'columns', name: 'Category' }
      ],
      dimension: 'users',
      reportsData: {}
    }
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    ActiveStatuses() {
      return this.expenseStatusOpts
        .map((itm) => itm.value)
        .filter((value) => this.IsStatusOn(value))
    },
    /* One row per person / project / category, summed over every status that is
       currently switched on. Amounts stay numeric so the table can sort them. */
    BreakdownRows() {
      const rows = new Map()
      this.ActiveStatuses.forEach((status) => {
        const source = this.reportsData?.[status]?.[this.dimension] ?? []
        source.forEach((item) => {
          const key = this.RowKey(item)
          if (!key) {
            return
          }
          const row = rows.get(key) || {
            uid: item.uid ?? null,
            id: item.uid ?? null,
            name: this.RowName(item),
            color: item.color ?? '#64748b',
            total: 0,
            gbp: 0,
            eur: 0,
            usd: 0
          }
          row.total += Number(item.total) || 0
          this.Currencies.forEach((currency) => {
            row[currency] += Number(item.amount?.[currency]) || 0
          })
          rows.set(key, row)
        })
      })
      return Array.from(rows.values())
    },
    /* Only show a currency column when something was actually spent in it. */
    ActiveCurrencies() {
      return this.Currencies.filter((currency) =>
        this.BreakdownRows.some((row) => row[currency] > 0)
      )
    },
    BreakdownFields() {
      const dim = this.dimensions.find((itm) => itm.key === this.dimension)
      return [
        { key: 'name', label: dim?.name ?? 'Name', sortable: true },
        { key: 'total', label: 'Requests', sortable: true, class: 'num-col' },
        ...this.ActiveCurrencies.map((currency) => ({
          key: currency,
          label: currency.toUpperCase(),
          sortable: true,
          class: 'num-col',
          formatter: (value) =>
            value
              ? this.CurrencySymbol[currency.toUpperCase()] +
                this.FormatAmount(value)
              : ''
        }))
      ]
    },
    BreakdownSortKey() {
      return this.ActiveCurrencies[0] ?? 'total'
    }
  },
  methods: {
    async fetchReports(filters = {}) {
      filters = Object.keys(filters).length
        ? {
            dateLabel: this.rangeEnums.dateLabel.findIndex(
              (label) => label === filters.dateLabel
            ),
            months: filters.months
              .map((month) =>
                this.rangeEnums.months.findIndex((mn) => mn === month)
              )
              .join(','),
            year: filters.year,
            status: filters.status.join(','),
            owner: filters.user,
            projectUid: filters.project,
            category: filters.category
          }
        : {}
      const { status, data } = await ServicesOld.GetExpenseReports(filters)
      if (status === 200) {
        this.reportsData = data.content.status
      }
    },
    FormatAmount(value) {
      return Number(value || 0).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    IsStatusOn(status) {
      return !!this.reportsLabelStatus[status]
    },
    StatusTotal(status) {
      return Number(this.reportsData?.[status]?.total) || 0
    },
    StatusAmounts(status) {
      const amounts = this.reportsData?.[status]?.amount ?? {}
      return this.Currencies.filter(
        (currency) => Number(amounts[currency]) > 0
      ).map((currency) => ({
        code: currency,
        symbol: this.CurrencySymbol[currency.toUpperCase()],
        value: this.FormatAmount(amounts[currency])
      }))
    },
    /* Toggling a tile drives both the local tile state and the status filter
       the expenses list reads out of the store. */
    ToggleStatus(status) {
      this.$set(this.reportsLabelStatus, status, !this.IsStatusOn(status))
      this.$store.commit('setSelectedExpenseStatus', this.ActiveStatuses)
    },
    RowKey(item) {
      return this.dimension === 'categories' ? item.label : item.uid
    },
    RowName(item) {
      return item.fullName ?? item.title ?? item.label ?? '-'
    },
    HasDimensionData(dimension) {
      return this.ActiveStatuses.some(
        (status) => (this.reportsData?.[status]?.[dimension] ?? []).length > 0
      )
    },
    /* Keep the tabs honest: never sit on a dimension that has no rows. */
    EnsureUsableDimension() {
      if (this.HasDimensionData(this.dimension)) {
        return
      }
      const fallback = this.dimensions.find((dim) =>
        this.HasDimensionData(dim.key)
      )
      if (fallback) {
        this.dimension = fallback.key
      }
    }
  },
  watch: {
    remoteFilters: {
      handler(val) {
        if (typeof val.status === 'undefined') {
          return
        }
        this.expenseStatusOpts.forEach((itm) => {
          this.$set(
            this.reportsLabelStatus,
            itm.value,
            val.status.includes(itm.value)
          )
        })
        this.fetchReports(val)
      },
      deep: true,
      immediate: true
    },
    reportsData() {
      this.EnsureUsableDimension()
    },
    ActiveStatuses() {
      this.EnsureUsableDimension()
    }
  }
}
</script>

<style>
/* The breakdown table is a b-table, so its inner DOM never gets this
   component's scope attribute — it has to be styled unscoped. The class is
   unique to this component, and it deliberately skips the shared
   `.lizard-expenses-box` treatment (navy header, 14px cells) that the previous
   version had to override rule by rule. */
.lizard-report-box {
  margin: 0 !important;
}
.lizard-report-box thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: #64748b !important;
  background: #fff !important;
  font-size: 10.5px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 10px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}
.lizard-report-box tbody td {
  color: #334155 !important;
  font-size: 12.5px !important;
  vertical-align: middle !important;
  padding: 7px 10px !important;
  border-top: 1px solid #f1f5f9 !important;
}
.lizard-report-box tbody tr:hover td {
  background-color: rgba(0, 115, 190, 0.05) !important;
}
.lizard-report-box .num-col {
  text-align: right;
  width: 110px;
  font-variant-numeric: tabular-nums;
}
.lizard-report-box thead th.num-col {
  text-align: right;
}
/* Project chips here are much smaller than the ones in the full-size tables. */
.lizard-report-box tbody td .selected-project {
  display: inline-block;
  min-width: 0 !important;
  max-width: 100%;
  padding: 2px 9px !important;
  margin: 0 !important;
  font-size: 11.5px !important;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
}
.lizard-report-box tbody td .row-link {
  cursor: pointer;
}
.lizard-report-box tbody td .row-link:hover {
  color: #0073be !important;
}
</style>

<style scoped>
.reports-panel {
  width: 100%;
  padding: 4px 8px 16px;
}

/* ---------------------------------------------------- status tiles */
.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.status-tile {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  padding: 14px 16px 13px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}
.status-tile:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.07);
}
.status-tile:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 115, 190, 0.18);
}
/* Switched-off statuses stay readable but visibly recede. */
.status-tile.off {
  background: #f8fafc;
  box-shadow: none;
}
.status-tile.off .tile-dot {
  background: #cbd5e1;
}
.status-tile.off .tile-toggle {
  color: #cbd5e1;
}
.status-tile.off .tile-label,
.status-tile.off .tile-count,
.status-tile.off .tile-amount {
  color: #94a3b8;
}

.tile-head {
  display: flex;
  align-items: center;
  gap: 7px;
}
.tile-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.tile-label {
  flex: 1 1 auto;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}
.tile-toggle {
  flex: 0 0 auto;
  background: none !important;
}
.tile-count {
  color: #0f172a;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.tile-count small {
  margin-left: 4px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
}
.tile-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
}
.tile-amount {
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.tile-amount.empty {
  color: #cbd5e1;
}

/* Status colours, carried by the dot and the toggle only — the card itself
   stays white so the numbers keep their contrast. The dot is a filled span,
   the toggle an svg that inherits `color`. */
.status-pending .tile-dot {
  background: #ffc23d;
}
.status-pending .tile-toggle {
  color: #e0a020;
}
.status-approved .tile-dot {
  background: #1aa2b8;
}
.status-approved .tile-toggle {
  color: #1aa2b8;
}
.status-rejected .tile-dot {
  background: #dc3b45;
}
.status-rejected .tile-toggle {
  color: #dc3b45;
}
.status-paid .tile-dot {
  background: #4aa746;
}
.status-paid .tile-toggle {
  color: #4aa746;
}

/* ---------------------------------------------------- breakdown */
.breakdown {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.breakdown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
}
.breakdown-title {
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.breakdown-sub {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: 8px;
  vertical-align: middle;
}
/* Which statuses the table is summing, as tinted pills that echo the colour
   of the tiles above rather than a lower-case run-on sentence. */
.status-badge {
  padding: 2px 8px;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.6;
  background: #f1f5f9;
  border-radius: 10px;
}
.status-badge.status-pending {
  color: #a06c05;
  background: rgba(255, 194, 61, 0.2);
}
.status-badge.status-approved {
  color: #10707f;
  background: rgba(26, 162, 184, 0.15);
}
.status-badge.status-rejected {
  color: #b32a33;
  background: rgba(220, 59, 69, 0.13);
}
.status-badge.status-paid {
  color: #2f7a2c;
  background: rgba(74, 167, 70, 0.15);
}
.status-badge.status-none {
  color: #94a3b8;
}
.dim-tabs {
  display: flex;
  gap: 2px;
  padding: 2px;
  background: #f1f5f9;
  border-radius: 8px;
}
.dim-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-radius: 6px;
  transition:
    color 0.15s ease,
    background 0.15s ease;
}
.dim-tab:hover:not(:disabled):not(.active) {
  color: #334155;
}
.dim-tab.active {
  color: #0073be;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.dim-tab:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}
.dim-tab:focus {
  outline: none;
}

.breakdown >>> .table-responsive {
  max-height: 340px;
  overflow-y: auto;
  margin: 0;
}
.breakdown-empty {
  padding: 26px 14px;
  color: #94a3b8;
  font-size: 12.5px;
  text-align: center;
}

/* Scrollbar for the breakdown list — the old one was `display: none`, which
   left the list scrollable with no affordance. */
.breakdown >>> .table-responsive::-webkit-scrollbar {
  width: 6px;
}
.breakdown >>> .table-responsive::-webkit-scrollbar-track {
  background: transparent;
}
.breakdown >>> .table-responsive::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.breakdown >>> .table-responsive::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
